import { Badge, Banner, Button, Checkbox, Field, Input, InputArea, Select } from '@cloudflare/kumo';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { ToolsTabs } from './toolCatalogShared';

interface Credential { id: string; name: string; provider: string; hint: string | null; lastUsedAt: string | null }
interface Schema { type: 'object'; properties: Record<string, { type?: string; description?: string }>; required?: string[] }
interface HttpTool {
  id: string; name: string; label: string; description: string; method: string; url: string; parameters: Schema;
  headers: Record<string, string>; authType: 'none' | 'bearer' | 'header' | 'query'; authName: string | null; credentialId: string | null;
  writes: boolean; timeoutMs: number; active: boolean; credential: { id: string; name: string; hint: string | null } | null;
}
interface Param { name: string; type: 'string' | 'number' | 'boolean'; description: string; required: boolean }
interface Draft {
  id?: string; name: string; label: string; description: string; method: string; url: string; params: Param[];
  headers: string; authType: HttpTool['authType']; authName: string; credentialId: string; writes: boolean; timeoutMs: number;
}
interface TestResult { ok: boolean; status: number; ms: number; url: string; body: unknown }

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const METHOD_OPTIONS = METHODS.map((m) => ({ value: m, label: m }));
const AUTH: Array<{ value: HttpTool['authType']; label: string }> = [
  { value: 'none', label: 'Sem autenticação' },
  { value: 'bearer', label: 'Bearer (Authorization)' },
  { value: 'header', label: 'Cabeçalho próprio' },
  { value: 'query', label: 'Parâmetro na URL' },
];
const AUTH_OPTIONS = AUTH.map((a) => ({ value: a.value, label: a.label }));
const PARAM_TYPE_OPTIONS = [
  { value: 'string', label: 'texto' },
  { value: 'number', label: 'número' },
  { value: 'boolean', label: 'sim/não' },
];

const emptyDraft = (): Draft => ({ name: '', label: '', description: '', method: 'GET', url: 'https://', params: [], headers: '', authType: 'none', authName: '', credentialId: '', writes: false, timeoutMs: 10000 });

const toDraft = (t: HttpTool): Draft => ({
  id: t.id, name: t.name, label: t.label, description: t.description, method: t.method, url: t.url,
  params: Object.entries(t.parameters?.properties ?? {}).map(([name, p]) => ({
    name, type: (['number', 'boolean'].includes(p.type ?? '') ? p.type : 'string') as Param['type'], description: p.description ?? '', required: !!t.parameters.required?.includes(name),
  })),
  headers: Object.entries(t.headers ?? {}).map(([k, v]) => `${k}: ${v}`).join('\n'),
  authType: t.authType, authName: t.authName ?? '', credentialId: t.credentialId ?? '', writes: t.writes, timeoutMs: t.timeoutMs,
});

const toSchema = (params: Param[]): Schema => ({
  type: 'object',
  properties: Object.fromEntries(params.filter((p) => p.name.trim()).map((p) => [p.name.trim(), { type: p.type, ...(p.description ? { description: p.description } : {}) }])),
  required: params.filter((p) => p.name.trim() && p.required).map((p) => p.name.trim()),
});

const parseHeaders = (raw: string) => Object.fromEntries(raw.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
  const i = l.indexOf(':');
  return i > 0 ? [l.slice(0, i).trim(), l.slice(i + 1).trim()] : [l, ''];
}));

/** IA › Ferramentas: APIs do cliente que os agentes podem chamar, e as credenciais (cofre) que elas usam */
export function AiToolsPage() {
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const canSecrets = can('integrations:manage');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [tools, setTools] = useState<HttpTool[] | null>(null);
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [vaultReady, setVaultReady] = useState(true);
  const [locked, setLocked] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [testArgs, setTestArgs] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TestResult | null>(null);
  const [newCred, setNewCred] = useState<{ name: string; value: string } | null>(null);
  const [deleting, setDeleting] = useState<HttpTool | null>(null);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/ai/http-tools');
    const j = await r.json().catch(() => ({}));
    if (r.status === 402 || r.status === 403) { setLocked(j.message || 'Recurso não incluído no seu plano'); return; }
    if (r.ok) setTools(j.tools);
    if (canSecrets) {
      const c = await fetchApi('/api/ai/credentials');
      if (c.ok) { const cj = await c.json(); setCredentials(cj.credentials); setVaultReady(cj.vaultReady); }
    }
  }, [fetchApi, canSecrets]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    const body = {
      name: draft.name.trim(), label: draft.label.trim() || draft.name.trim(), description: draft.description, method: draft.method, url: draft.url.trim(),
      parameters: toSchema(draft.params), headers: parseHeaders(draft.headers), authType: draft.authType,
      authName: draft.authName.trim() || null, credentialId: draft.credentialId || null, writes: draft.writes, timeoutMs: draft.timeoutMs,
    };
    const r = await fetchApi(draft.id ? `/api/ai/http-tools/${draft.id}` : '/api/ai/http-tools', { method: draft.id ? 'PUT' : 'POST', body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível salvar'); return; }
    toast.success('Ferramenta salva. Ligue-a no agente em IA › Agentes › Ferramentas.');
    setDraft(toDraft({ ...j.tool, credential: null }));
    load();
  };

  const test = async () => {
    if (!draft?.id) return;
    setBusy(true); setResult(null);
    const args = Object.fromEntries(draft.params.filter((p) => p.name.trim() && testArgs[p.name] !== undefined && testArgs[p.name] !== '').map((p) => {
      const v = testArgs[p.name];
      return [p.name, p.type === 'number' ? Number(v) : p.type === 'boolean' ? v === 'true' : v];
    }));
    const r = await fetchApi(`/api/ai/http-tools/${draft.id}/test`, { method: 'POST', body: JSON.stringify({ args }) });
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'A chamada falhou'); return; }
    setResult(j.result);
  };

  const addCredential = async () => {
    if (!newCred) return;
    const r = await fetchApi('/api/ai/credentials', { method: 'POST', body: JSON.stringify(newCred) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.message || 'Não foi possível guardar'); return; }
    toast.success('Credencial guardada no cofre');
    setNewCred(null);
    await load();
    if (draft && !draft.credentialId) setDraft({ ...draft, credentialId: j.credential.id });
  };

  const removeCredential = async (c: Credential) => {
    const r = await fetchApi(`/api/ai/credentials/${c.id}`, { method: 'DELETE' });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { toast.error(j.message || 'Não foi possível apagar'); return; }
    load();
  };

  if (locked) return <div className="page-content" style={{ paddingTop: 28 }}><Banner variant="default" description={<>{locked}</>} /></div>;

  const setParam = (i: number, p: Partial<Param>) => draft && setDraft({ ...draft, params: draft.params.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const credentialOptions = credentials.filter((c) => c.provider === 'http').map((c) => ({ value: c.id, label: `${c.name} (${c.hint})` }));

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <ToolsTabs active="minhas" />
        <div className="flex items-start justify-between gap-4">
          <div style={{ maxWidth: 760 }}>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Ferramentas</h2>
            <div className="text-kumo-subtle">
              Conecte os agentes às APIs da sua empresa: consultar pedido, ver estoque, abrir chamado… A chamada sai do nosso servidor, com a credencial guardada no cofre.
              Depois de criar, ligue a ferramenta no agente em <Link to="/ia/agentes">IA › Agentes</Link>.
            </div>
          </div>
          {canEdit && !draft && <Button variant="primary" icon={ICONS.Plus} onClick={() => { setDraft(emptyDraft()); setResult(null); setTestArgs({}); }} data-testid="ai-tools-new">Nova ferramenta</Button>}
        </div>
      </div>

      <div className="page-content space-y-6">
        {!vaultReady && <Banner variant="alert" description={<>O cofre de credenciais não está configurado neste ambiente (VAULT_MASTER_KEY). Ferramentas sem autenticação funcionam normalmente.</>} />}

        {!draft && tools && tools.length === 0 && <Banner variant="default" description={<>Nenhuma ferramenta ainda. Crie uma para o agente consultar ou registrar dados no seu sistema.</>} />}
        {!draft && tools && tools.length > 0 && (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {tools.map((t) => (
              <section key={t.id} className="brand-card space-y-2" data-testid={`ai-tool-card-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold">{t.label}</div>
                  <div className="flex gap-1">
                    {t.writes && <Badge variant="orange">grava dados</Badge>}
                    <Badge variant={t.active ? 'green' : 'neutral'}>{t.active ? 'Ativa' : 'Pausada'}</Badge>
                  </div>
                </div>
                <div className="text-xs" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{t.method} {t.url}</div>
                <div className="text-sm !text-[var(--text-muted)]">{t.description}</div>
                <div className="text-xs !text-[var(--text-muted)]">{t.authType === 'none' ? 'Sem autenticação' : `Credencial: ${t.credential?.name ?? '—'}`}</div>
                <div className="flex gap-2 pt-1">
                  <Button variant="secondary" icon={canEdit ? ICONS.Edit : ICONS.Visibility} onClick={() => { setDraft(toDraft(t)); setResult(null); setTestArgs({}); }}>{canEdit ? 'Editar e testar' : 'Ver'}</Button>
                  {canEdit && <DangerButton variant="dangerOutline" icon={ICONS.Trash}  onClick={() => setDeleting(t)}>Excluir</DangerButton>}
                </div>
              </section>
            ))}
          </div>
        )}

        {draft && (
          <div className="grid gap-6" style={{ gridTemplateColumns: 'minmax(0, 3fr) minmax(300px, 2fr)' }}>
            <section className="brand-card space-y-4">
              <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <Field label="Nome para o agente" description="snake_case, ex.: consultar_pedido">
                  <Input onChange={(e) => setDraft({ ...draft, name: e.target.value })} value={draft.name} />
                </Field>
                <Field label="Nome na tela">
                  <Input onChange={(e) => setDraft({ ...draft, label: e.target.value })} value={draft.label} placeholder="Consultar pedido" />
                </Field>
              </div>
              <InputArea label="Quando usar" description="O agente decide pela descrição: diga o que a ferramenta faz e quando chamar." onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={3} value={draft.description} />
              <div className="grid gap-3" style={{ gridTemplateColumns: '120px 1fr' }}>
                <Field label="Método">
                  <Select value={(METHOD_OPTIONS.find((m) => m.value === draft.method) ?? null)?.value ?? null} items={METHOD_OPTIONS} onValueChange={(value: string | null) => {
                      const v = value;
                      if (v) setDraft({ ...draft, method: v });
                    }} />
                </Field>
                <Field label="Endereço" description="https, endereço público. Use {parametro} no caminho.">
                  <Input onChange={(e) => setDraft({ ...draft, url: e.target.value })} value={draft.url} placeholder="https://api.suaempresa.com/pedidos/{numero}" />
                </Field>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold" style={{ fontSize: 15 }}>Parâmetros</h3>
                  <Button variant="secondary" onClick={() => setDraft({ ...draft, params: [...draft.params, { name: '', type: 'string', description: '', required: true }] })}>Adicionar</Button>
                </div>
                <div className="text-xs !text-[var(--text-muted)]">O agente preenche estes valores. Os que aparecem como {'{nome}'} no endereço vão no caminho; os outros, na query (GET/DELETE) ou no corpo JSON.</div>
                {draft.params.map((p, i) => (
                  <div key={i} className="grid gap-2 items-end" style={{ gridTemplateColumns: '1fr 110px 2fr auto auto' }}>
                    <Input onChange={(e) => setParam(i, { name: e.target.value })} aria-label="Nome do parâmetro" placeholder="numero" value={p.name} />
                    <Select value={(PARAM_TYPE_OPTIONS.find((t) => t.value === p.type) ?? null)?.value ?? null} items={PARAM_TYPE_OPTIONS} onValueChange={(value: string | null) => {
                        const v = value;
                        if (v) setParam(i, { type: v as Param['type'] });
                      }} aria-label="Tipo" />
                    <Input onChange={(e) => setParam(i, { description: e.target.value })} aria-label="Descrição do parâmetro" placeholder="Número do pedido, como o cliente informou" value={p.description} />
                    <Checkbox onCheckedChange={(checked: boolean) => setParam(i, { required: checked })} checked={p.required} label={<>obrigatório</>} />
                    <DangerButton variant="dangerOutline" aria-label="Remover parâmetro" onClick={() => setDraft({ ...draft, params: draft.params.filter((_, j) => j !== i) })}>×</DangerButton>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <h3 className="text-lg font-semibold" style={{ fontSize: 15 }}>Autenticação</h3>
                <div className="grid gap-3" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <Field label="Tipo">
                    <Select value={(AUTH_OPTIONS.find((a) => a.value === draft.authType) ?? null)?.value ?? null} items={AUTH_OPTIONS} onValueChange={(value: string | null) => {
                        const v = value;
                        if (v) setDraft({ ...draft, authType: v as Draft['authType'] });
                      }} />
                  </Field>
                  {(draft.authType === 'header' || draft.authType === 'query') && (
                    <Field label={draft.authType === 'header' ? 'Nome do cabeçalho' : 'Nome do parâmetro'}>
                      <Input onChange={(e) => setDraft({ ...draft, authName: e.target.value })} placeholder={draft.authType === 'header' ? 'X-Api-Key' : 'api_key'} value={draft.authName} />
                    </Field>
                  )}
                </div>
                {draft.authType !== 'none' && (
                  <Field label="Credencial">
                    <Select value={(credentialOptions.find((c) => c.value === draft.credentialId) ?? null)?.value ?? null} items={credentialOptions} onValueChange={(value: string | null) => {
                        const v = value;
                        setDraft({ ...draft, credentialId: v ?? '' });
                      }} placeholder="Escolha…" />
                    {!canSecrets && <div className="text-xs !text-[var(--text-muted)]">Credenciais são cadastradas por quem administra as integrações da organização.</div>}
                  </Field>
                )}
              </div>

              <InputArea label="Cabeçalhos fixos (opcional)" description="Um por linha, Nome: valor. Sem segredos: eles vão numa credencial." onChange={(e) => setDraft({ ...draft, headers: e.target.value })} rows={2} value={draft.headers} />
              <Checkbox onCheckedChange={(checked: boolean) => setDraft({ ...draft, writes: checked })} checked={draft.writes} labelTooltip="No teste do agente (simulação) ela não é chamada; só mostra o que faria." label={<>
                Grava ou altera dados no sistema
              </>} />

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setDraft(null)}>Fechar</Button>
                {canEdit && <Button variant="primary" loading={busy} disabled={!draft.name.trim()} onClick={save} data-testid="ai-tools-save">Salvar</Button>}
              </div>
            </section>

            <div className="space-y-6">
              <section className="brand-card space-y-3" data-testid="ai-tools-test">
                <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Testar chamada</h3>
                {!draft.id ? <div className="text-sm !text-[var(--text-muted)]">Salve para testar com a API de verdade.</div> : (
                  <>
                    <div className="text-xs !text-[var(--text-muted)]">Chama a API agora, com a credencial. Ferramentas que gravam dados gravam de verdade aqui.</div>
                    {draft.params.filter((p) => p.name.trim()).map((p) => (
                      <Field label={p.name} description={p.description} key={p.name}>
                        <Input onChange={(e) => setTestArgs({ ...testArgs, [p.name]: e.target.value })} value={testArgs[p.name] ?? ''} />
                      </Field>
                    ))}
                    <Button variant="primary" icon={ICONS.Play} loading={busy} onClick={test} data-testid="ai-tools-run">Chamar</Button>
                    {result && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2"><Badge variant={result.ok ? 'green' : 'red'}>{result.status}</Badge><div className="text-xs !text-[var(--text-muted)]">{result.ms} ms</div></div>
                        <div className="text-xs" style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{result.url}</div>
                        <pre className="text-xs" style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-2)', padding: 8, borderRadius: 6, maxHeight: 320, overflow: 'auto' }}>
                          {typeof result.body === 'string' ? result.body : JSON.stringify(result.body, null, 2)}
                        </pre>
                      </div>
                    )}
                  </>
                )}
              </section>

              {canSecrets && (
                <section className="brand-card space-y-3" data-testid="ai-credentials">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Credenciais</h3>
                    {!newCred && <Button variant="secondary" icon={ICONS.Plus} disabled={!vaultReady} onClick={() => setNewCred({ name: '', value: '' })}>Nova</Button>}
                  </div>
                  <div className="text-xs !text-[var(--text-muted)]">Tokens e chaves de API ficam cifrados no cofre. Depois de guardar, ninguém vê o valor de novo; só dá para trocar.</div>
                  {newCred && (
                    <div className="space-y-2">
                      <Field label="Nome">
                        <Input onChange={(e) => setNewCred({ ...newCred, name: e.target.value })} placeholder="API da loja" value={newCred.name} />
                      </Field>
                      <Field label="Valor">
                        <Input onChange={(e) => setNewCred({ ...newCred, value: e.target.value })} type="password" autoComplete="off" value={newCred.value} />
                      </Field>
                      <div className="flex justify-end gap-2">
                        <Button variant="secondary" onClick={() => setNewCred(null)}>Cancelar</Button>
                        <Button variant="primary" disabled={!newCred.name.trim() || !newCred.value.trim()} onClick={addCredential} data-testid="ai-credentials-save">Guardar</Button>
                      </div>
                    </div>
                  )}
                  <ul className="space-y-1">
                    {credentials.map((c) => (
                      <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
                        <span>{c.name} <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>{c.hint}</span></span>
                        <span className="flex items-center gap-2">
                          <div className="text-xs !text-[var(--text-muted)]">{c.lastUsedAt ? `usada ${new Date(c.lastUsedAt).toLocaleDateString('pt-BR')}` : 'nunca usada'}</div>
                          <DangerButton variant="dangerOutline" onClick={() => removeCredential(c)}>Apagar</DangerButton>
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          </div>
        )}
      </div>

      <DangerConfirmModal open={!!deleting} title="Excluir ferramenta" confirmText="Excluir" objectName={deleting?.label}
        onCancel={() => setDeleting(null)} onConfirm={async () => { if (deleting) { const r = await fetchApi(`/api/ai/http-tools/${deleting.id}`, { method: 'DELETE' }); if (r.ok) load(); } setDeleting(null); }}>
        Os agentes que usam <strong>{deleting?.label}</strong> deixam de ter essa ferramenta. As versões já publicadas param de conseguir chamá-la.
      </DangerConfirmModal>
    </>
  );
}
